import React, { useEffect } from 'react';
import { 
  Box, 
  Grid, 
  VStack, 
  Heading, 
  Text,
  SimpleGrid,
  HStack,
  Icon,
  Flex,
  useColorModeValue,
  Circle,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Users, Heart, Network, MessageCircle, CheckCircle } from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import UnifiedMobilePlatform from '../ui-samples/UnifiedMobilePlatform';
import { useAudio } from '../../context/AudioContext';

// Methodist color palette
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';
const methodistPurple = '#805AD5';

/**
 * Presenter's Guide for CultureDeepDive Slide:
 * 
 * 1. Introduction (30 seconds):
 *    - Introduce the Cultural Quotient (CQ) Domain as the second pillar
 *    - Emphasize how this domain builds supportive community connections
 *    - Highlight the importance of peer support in preventing burnout
 * 
 * 2. Key Features Section (45 seconds):
 *    - Point out the four key features of the CQ Domain:
 *      a. Community Events: Regular wellness activities and gatherings
 *      b. Peer Connections: Meaningful professional relationships
 *      c. Support Networks: Emotional and social support systems
 *      d. Group Challenges: Collaborative wellness initiatives
 * 
 * 3. Engagement Metrics (30 seconds):
 *    - Explain the three key community metrics tracked
 *    - Emphasize the benefits of active community participation
 * 
 * 4. Implementation Approach (30 seconds):
 *    - Walk through the community building process
 *    - Stress the organic growth of support networks
 * 
 * 5. Mobile Experience Demo (1 minute):
 *    - Showcase the mobile interface for community engagement
 *    - Demonstrate how clinicians can connect and participate
 *    - Highlight the Cultural Quotient Assistant features
 * 
 * 6. Conclusion (15 seconds):
 *    - Summarize how the CQ Domain strengthens professional relationships
 *    - Transition to the Practice Efficiency Digital Interface
 */

// Full narration script for TTS Studio by 11Labs
const narrationScript = `Let's explore the Cultural Quotient (CQ) domain of the SentientIQ platform, which enhances team dynamics and cultural awareness in healthcare settings.

Our key features focus on three core areas:
1. Peer Support - connecting colleagues through AI-powered matching, with 92% engagement in mentoring
2. Wellness Groups - specialty-focused communities for mutual support, achieving 85% participation
3. Community Growth - building meaningful connections across specialties, showing 78% network expansion

Our implementation approach follows a proven three-step process:
1. Team Assessment - evaluating current cultural dynamics and support needs
2. Engagement Plan - designing targeted activities for peer connections
3. Growth Tracking - monitoring community engagement and relationships

For clinicians, the benefits are substantial:
- Access to peer mentors and support groups
- Specialty-focused community connections
- Cross-department collaboration networks
- Professional relationship building
- Enhanced team communication

Our key metrics demonstrate strong performance:
- Community score at 85/90
- Support rating at 92/95
- Network growth at 68/75

The mobile interface provides intuitive access to all these features, optimized for healthcare environments.

Next, we'll explore the Workplace Quotient domain, which complements cultural intelligence with operational excellence.`;

export const CultureDeepDive: React.FC = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // Use the audio context
  const { playAudioForSlide } = useAudio();

  // Play audio when component mounts
  useEffect(() => {
    // Play audio with a slight delay to ensure DOM is ready
    const audioTimer = setTimeout(() => {
      console.log('Starting audio playback for culture slide...');
      playAudioForSlide('culture-deepdive');
    }, 800);
    
    // Cleanup function
    return () => {
      clearTimeout(audioTimer);
      // Audio cleanup is handled by AudioContext
    };
  }, [playAudioForSlide]);

  const features = [
    {
      icon: Users,
      title: "Peer Support",
      description: "Connect with colleagues facing similar challenges through AI-powered matching",
      color: methodistBlue,
      impactMetric: "92% engagement",
      scalabilityInfo: "Active mentoring"
    },
    {
      icon: Heart,
      title: "Wellness Groups",
      description: "Join specialty-focused communities for mutual support",
      color: methodistGreen,
      impactMetric: "85% participation",
      scalabilityInfo: "Group dynamics"
    },
    {
      icon: Network,
      title: "Community Growth",
      description: "Build meaningful connections across specialties and departments",
      color: methodistPurple,
      impactMetric: "78% expansion",
      scalabilityInfo: "Network reach"
    }
  ];

  return (
    <MethodistSlide showLogo={true} captionsText={narrationScript}>
      <Grid templateColumns="2fr 1fr" gap={8} h="100%" py={6}>
        {/* Left side: Features and content */}
        <VStack align="stretch" spacing={8}>
          {/* Introduction */}
          <Box>
            <Heading size="md" color={methodistBlue} mb={3}>
              Cultural Quotient (CQ) Domain
            </Heading>
            <Text fontSize="md" color={textColor}>
              The SentientIQ Cultural Quotient (CQ) platform enhances team dynamics and cultural awareness in healthcare settings. 
              Our mobile interface facilitates meaningful connections and professional growth through collaborative learning.
            </Text>
          </Box>

          {/* Key Features */}
          <Box>
            <Heading size="sm" mb={4}>Key Features</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {features.map((feature, index) => (
                <HStack
                  key={index}
                  spacing={4}
                  p={4}
                  bg={bgColor}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  shadow="sm"
                  as={motion.div}
                  whileHover={{ scale: 1.02 }}
                  transition={{ ease: "easeInOut" }}
                >
                  <Flex
                    align="center"
                    justify="center"
                    bg={`${feature.color}15`}
                    color={feature.color}
                    borderRadius="md"
                    p={2}
                    boxSize="40px"
                  >
                    <Icon as={feature.icon} boxSize={5} />
                  </Flex>
                  <VStack align="start" spacing={1} flex={1}>
                    <Text fontWeight="medium" fontSize="sm">
                      {feature.title}
                    </Text>
                    <Text fontSize="xs" color={textColor}>
                      {feature.description}
                    </Text>
                    <HStack fontSize="xs" spacing={3} mt={1}>
                      <Text color={feature.color} fontWeight="medium">
                        {feature.impactMetric}
                      </Text>
                      <Text color="gray.500">
                        {feature.scalabilityInfo}
                      </Text>
                    </HStack>
                  </VStack>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          {/* Implementation Steps and Benefits Grid */}
          <SimpleGrid columns={2} spacing={8}>
            {/* Left Column: Implementation Steps */}
            <Box>
              <Heading size="sm" mb={4}>Implementation Approach</Heading>
              <VStack spacing={4}>
                {[
                  {
                    title: "Team Assessment",
                    description: "Evaluate current cultural dynamics and support needs",
                    color: methodistBlue
                  },
                  {
                    title: "Engagement Plan",
                    description: "Design targeted activities for peer connections",
                    color: methodistGreen
                  },
                  {
                    title: "Growth Tracking",
                    description: "Monitor community engagement and relationships",
                    color: methodistPurple
                  }
                ].map((step, index) => (
                  <Box
                    key={index}
                    p={4}
                    bg={bgColor}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    shadow="sm"
                    w="full"
                    as={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <HStack spacing={4} align="start">
                      <Circle size="24px" bg={`${step.color}15`}>
                        <Text fontSize="xs" fontWeight="bold" color={step.color}>
                          {index + 1}
                        </Text>
                      </Circle>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontWeight="semibold"
                          fontSize="sm"
                          color={step.color}
                        >
                          {step.title}
                        </Text>
                        <Text fontSize="xs" color={textColor}>
                          {step.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>

            {/* Right Column: Benefits and Metrics */}
            <VStack spacing={6}>
              {/* Clinician Benefits */}
              <Box w="full">
                <Heading size="sm" mb={4}>Clinician Benefits</Heading>
                <VStack 
                  align="start" 
                  spacing={3} 
                  bg={bgColor} 
                  p={4} 
                  borderRadius="md" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                  shadow="sm"
                >
                  {[
                    "Access to peer mentors and support groups",
                    "Specialty-focused community connections",
                    "Cross-department collaboration networks",
                    "Professional relationship building",
                    "Enhanced team communication"
                  ].map((benefit, index) => (
                    <HStack key={index} spacing={3} w="full">
                      <Circle size="16px" bg={`${methodistBlue}15`}>
                        <Icon as={CheckCircle} boxSize={3} color={methodistBlue} />
                      </Circle>
                      <Text fontSize="xs" color={textColor}>
                        {benefit}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* Key Metrics */}
              <Box w="full">
                <Heading size="sm" mb={4}>Key Metrics</Heading>
                <HStack spacing={3}>
                  {[
                    {
                      label: "Community",
                      value: "85",
                      goal: "90",
                      color: methodistBlue,
                      icon: Users
                    },
                    {
                      label: "Support",
                      value: "92",
                      goal: "95",
                      color: methodistGreen,
                      icon: Heart
                    },
                    {
                      label: "Network",
                      value: "68",
                      goal: "75",
                      color: methodistPurple,
                      icon: Network
                    }
                  ].map((metric, index) => (
                    <Box
                      key={index}
                      p={3}
                      bg={bgColor}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                      shadow="sm"
                      flex={1}
                    >
                      <VStack spacing={2}>
                        <Icon as={metric.icon} color={metric.color} boxSize={4} />
                        <Text fontSize="xs" color={textColor} noOfLines={1}>
                          {metric.label}
                        </Text>
                        <HStack spacing={1}>
                          <Text fontSize="sm" fontWeight="bold" color={metric.color}>
                            {metric.value}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            /{metric.goal}
                          </Text>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </HStack>
              </Box>
            </VStack>
          </SimpleGrid>
        </VStack>

        {/* Right side: Mobile UI Demo */}
        <VStack align="start" spacing={4}>
          <Box>
            <Heading size="sm" mb={2}>Mobile Platform Demo</Heading>
            <Text fontSize="sm" color={textColor}>
              Experience the SentientIQ mobile interface in action. Our intuitive design facilitates team collaboration, 
              cultural learning, and professional networking—all optimized for healthcare environments.
            </Text>
          </Box>
          <Box 
            bg={bgColor}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            shadow="sm"
            p={4}
            w="full"
          >
            <UnifiedMobilePlatform 
              activeDomain="CQ"
              defaultTab="activities"
            />
          </Box>
        </VStack>
      </Grid>
    </MethodistSlide>
  );
};

export default CultureDeepDive;
